import { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

import { CONTEXT_MENU_CELL_ID } from '@/shared/components/ui/data-table/components/ag-grid-table-core/ag-grid-table-core.constants';
import { useGroupsConfig } from '@/shared/features/groups/context/groups-config-context';
import { TGroupListActionsRenderer } from '@/shared/features/groups/context/groups-config-context.types';
import { useGroupColumn } from '@/shared/features/groups/hooks/use-group-column/use-group-column';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import {
  FirstNameRenderer,
  LastNameRenderer,
  UserActionsRenderer,
  UserIdentificationRenderer,
  UserRolesRenderer,
  UserSourceRenderer,
} from '@/shared/features/rbac/components/users-list/users-list.renderer';

// Constants for column widths
const COLUMN_WIDTHS = {
  firstName: { min: 120 },
  lastName: { min: 120 },
  companyName: { min: 150 },
  email: { min: 200 },
  userRoles: { min: 150 },
  group: { min: 200 },
  source: { min: 120 },
  identification: { fixed: 150 },
  actions: { fixed: 200 },
} as const;

// Base column configuration factory
const createBaseColumns = (t: (key: string) => string) => ({
  firstName: (): ColDef => ({
    field: 'firstName',
    headerName: t('rbac:first_name'),
    minWidth: COLUMN_WIDTHS.firstName.min,
    cellRenderer: FirstNameRenderer,
    sortable: true,
  }),
  lastName: (): ColDef => ({
    field: 'lastName',
    headerName: t('rbac:last_name'),
    cellRenderer: LastNameRenderer,
    minWidth: COLUMN_WIDTHS.lastName.min,
    sortable: true,
  }),
  companyName: (): ColDef => ({
    field: 'companyName',
    headerName: t('rbac:company'),
    minWidth: COLUMN_WIDTHS.companyName.min,
    sortable: true,
  }),
  email: (): ColDef => ({
    field: 'email',
    headerName: t('rbac:email'),
    minWidth: COLUMN_WIDTHS.email.min,
    sortable: true,
  }),
  userRoles: (): ColDef => ({
    field: 'userRoles',
    headerName: t('rbac:roles'),
    cellRenderer: UserRolesRenderer,
    minWidth: COLUMN_WIDTHS.userRoles.min,
    sortable: false,
  }),
});

// User-specific column definitions
const createUserColumns = (t: (key: string) => string) => ({
  source: (): ColDef => ({
    field: 'source',
    headerName: t('common:source'),
    cellRenderer: UserSourceRenderer,
    minWidth: COLUMN_WIDTHS.source.min,
    sortable: true,
  }),
  identification: (): ColDef => ({
    field: 'identification',
    headerName: '',
    cellRenderer: UserIdentificationRenderer,
    width: COLUMN_WIDTHS.identification.fixed,
    minWidth: COLUMN_WIDTHS.identification.fixed,
    sortable: false,
    resizable: false,
  }),
  actions: (render?: any): ColDef => ({
    field: CONTEXT_MENU_CELL_ID,
    headerName: '',
    cellRenderer: render,
    width: COLUMN_WIDTHS.actions.fixed,
    minWidth: COLUMN_WIDTHS.actions.fixed,
    sortable: false,
    resizable: false,
  }),
});

/**
 * Hook for Users List column definitions
 * @param ssoEnabled - Whether to include the source column
 * @param hideActionsColumn - Whether to hide the actions column
 */
export const useUsersListDefinitions = (
  ssoEnabled?: boolean,
  hideActionsColumn = false,
): ColDef[] => {
  const { t } = useTranslation();
  const groupColumn = useGroupColumn();

  const base = createBaseColumns(t);
  const user = createUserColumns(t);

  const columnDefs: ColDef[] = [
    base.firstName(),
    base.lastName(),
    base.companyName(),
    base.email(),
    base.userRoles(),
    groupColumn,
  ];

  if (ssoEnabled) {
    columnDefs.push(user.source());
  }

  if (!hideActionsColumn) {
    columnDefs.push(user.actions(UserActionsRenderer));
  }

  return columnDefs;
};

export const useGroupUsersListDefinitions = (options?: {
  ssoEnabled?: boolean;
  actionsRenderer?: TGroupListActionsRenderer;
}): ColDef[] => {
  const { t } = useTranslation();
  const { ssoEnabled, actionsRenderer } = options ?? {};
  const { can } = usePermissionCheck();
  const { permissionActions } = useGroupsConfig();
  // Row actions here move/remove users between groups → group edit. Drop the
  // column when the user can manage no group at all.
  const canManageGroup = can(permissionActions.updateGroup);

  const base = createBaseColumns(t);
  const user = createUserColumns(t);

  const columnDefs: ColDef[] = [
    base.firstName(),
    base.lastName(),
    base.companyName(),
    base.email(),
    base.userRoles(),
  ];

  if (ssoEnabled) {
    columnDefs.push(user.source());
  }

  if (actionsRenderer && canManageGroup) {
    columnDefs.push(user.actions(actionsRenderer));
  }

  return columnDefs;
};
